import React, { useEffect } from 'react';
import useReconciliationService from 'domain/finance/costAccounting/reconcilation/useReconciliationService';
import ReconciliationStaticFilters from 'domain/finance/costAccounting/reconcilation/shared/filter/ReconciliationStaticFilters';
import { OfficeCodeFilter } from 'domain/shared/employee';
import ReconciliationStatusFilter from 'domain/finance/costAccounting/reconcilation/shared/filter/ReconciliationStatusFilter';
import NotReconciliatedTransfersLists from './NotReconciliatedTransfersLists';
import UploadFileButton from 'domain/finance/costAccounting/reconcilation/shared/button/UploadFileButton';
import { Filters, FiltersLayout, ListHeader, ListPageLayout, useListContext } from 'framework/list';
import ReconciliationGroupsContents from './ReconciliationGroupsContents';
import ReconciliationDropdownFilter from '../shared/input/ReconciliationDropdownFilter';
import { TabsWrapper } from 'framework/ui/TabsWrapper';
import type { ReconciliationListPageContentProps, UpdateListDataAndReloadGroupsProps } from './types';

const filters = (
  <FiltersLayout>
    <OfficeCodeFilter source="office" label="Office" />
    <ReconciliationStatusFilter source="status" label="Status" />
    <ReconciliationDropdownFilter source="type" label="Type" />
  </FiltersLayout>
);

const ReconciliationListPageContent = ({
  isSensitiveDataIsShown,
  toggleSensitiveDataButton,
  bankData,
  bankLoading,
  davinciData,
  davinciLoading,
  reloadData = () => {},
  reloadSensitiveData,
  updateListDataAndReloadGroups,
}: ReconciliationListPageContentProps & {
  reloadSensitiveData?: () => void;
  toggleSensitiveDataButton?: Nullable<React.ReactElement>;
  updateListDataAndReloadGroups: (data: UpdateListDataAndReloadGroupsProps) => void;
}) => {
  const { getFilterValidationSchema } = useReconciliationService();
  const { data, loading } = useListContext();

  useEffect(() => {
    if (isSensitiveDataIsShown && reloadSensitiveData && !loading && !davinciLoading && !bankLoading) {
      reloadSensitiveData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.items, loading, davinciLoading, bankLoading]);

  return (
    <Filters getValidationSchema={getFilterValidationSchema}>
      <ListPageLayout
        header={
          <ReconciliationStaticFilters title={<ListHeader label="Reconciliation" />}>
            {toggleSensitiveDataButton}
            <UploadFileButton execute={reloadData} />
          </ReconciliationStaticFilters>
        }
        filter={filters}
      >
        <TabsWrapper
          tabs={[
            {
              label: 'Not Reconciliated Transfers',
              content: (
                <NotReconciliatedTransfersLists
                  isSensitiveDataIsShown={isSensitiveDataIsShown}
                  bankData={bankData}
                  bankLoading={bankLoading}
                  davinciData={davinciData}
                  davinciLoading={davinciLoading}
                  updateListDataAndReloadGroups={updateListDataAndReloadGroups}
                />
              ),
            },
            {
              label: 'Reconciliation Groups',
              content: (
                <ReconciliationGroupsContents
                  bankData={bankData}
                  davinciData={davinciData}
                  isSensitiveDataIsShown={isSensitiveDataIsShown}
                  updateListDataAndReloadGroups={updateListDataAndReloadGroups}
                  reloadData={reloadData}
                />
              ),
            },
          ]}
        />
      </ListPageLayout>
    </Filters>
  );
};

export default ReconciliationListPageContent;
